import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { apiService, FILES_BASE_URL } from "../api/apiService";
import {
    naRgb, naHex, lepszyAtrament, tokenyAkcentuCiemnego, jakoDeklaracje,
    ATRAMENT_JASNY, ATRAMENT_CIEMNY_JASNEGO_MOTYWU,
} from "../utils/kontrastKolorow";
import { ATRYBUT_KOLORU_MARKI, ustawKolorPaskaPrzegladarki } from "../utils/motywAplikacji";

/**
 * Globalna tożsamość aplikacji (branding). Pobiera /api/branding (publiczny) i stosuje RUNTIME:
 * document.title, favicon, theme-color, meta description, og/twitter (w aplikacji) oraz wystawia
 * logoUrl/appName do użycia „masowo" (Sidebar, Login itd.) — zmiana w 1 miejscu propaguje wszędzie.
 * Podgląd linku u crawlerów czyta statyczny index.html (og:image wskazuje stały endpoint backendu).
 */
const BrandingContext = createContext(null);

// DTO zwraca ścieżki względne (/api/branding/asset/...). Budujemy absolutny URL backendu.
const absUrl = (path) => (path ? `${FILES_BASE_URL}${path}` : null);

// Modyfikuje istniejący tag meta z index.html (nie tworzy nowych).
const setMeta = (selector, value) => {
    if (value == null) return;
    const el = document.head.querySelector(selector);
    if (el) el.setAttribute("content", value);
};

/** `#rrggbb` albo `#rgb` → „r, g, b". Null dla czegokolwiek innego — nie zgadujemy. */
const naSkladowe = (hex) => {
    if (typeof hex !== "string") return null;
    const m = hex.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!m) return null;
    const h = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1];
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(", ");
};

/**
 * [polecenie właściciela 2026-10-03, poprawka po przeglądzie 2026-10-04] „kolor domyślny aplikacji to biały, nie ciemny”.
 * CZERŃ I BIEL Z PANELU = „NIE USTAWIONO” — ta sama reguła co w mailach (EmailTemplateHelper.PoprawnyKolorLubDomyslny).
 * Pole „Kolor motywu (theme-color)” opisuje pasek przeglądarki, a czerń jest jego typową wartością z szablonu strony.
 * Na produkcji stoi tam #000000 (pomiar 6.09). BYŁO ŹLE: ten kolor szedł wprost w akcent, więc każdy bez własnej palety
 * i bez palety spółki miał czarne przyciski, czarne zaznaczenia i czarny pasek przeglądarki na telefonie — aplikacja
 * wyglądała na ciemną także w jasnym motywie (to samo źródło dawało wcześniej czarne belki na wydrukach struktury
 * i w mailach, które właściciel reklamował). Teraz czerń/biel = paleta domyślna (granat) i pasek za motywem;
 * każdy realny kolor marki działa jak dotąd. Kto chce czarnego akcentu, ma paletę „Czarny” (osobistą albo spółki).
 * Zwraca `#rrggbb`/`#rgb` albo null.
 */
export const kolorMarkiZPanelu = (hex) => {
    if (!naSkladowe(hex)) return null;
    const kandydat = hex.trim().toLowerCase().replace(/^#?/, "#");
    if (["#000", "#000000", "#fff", "#ffffff"].includes(kandydat)) return null;
    return kandydat;
};

/**
 * Kolor firmowy z panelu Branding jako DOMYŚLNY akcent interfejsu.
 *
 * Ustawiamy tokeny na `<html>` stylem bezpośrednim, więc biją domyślne z `:root`,
 * ale przegrywają z `html[data-accent="…"]` z arkusza — i to jest właściwa kolejność:
 * instytucja ustawia domyślny wygląd, a użytkownik może go u siebie nadpisać.
 *
 * Kolor tekstu NA akcencie liczymy z jasności, bo firmowy kolor bywa jasny (złoto),
 * a biały napis na jasnym tle jest nieczytelny — dokładnie ten błąd miały już
 * palety morska, bursztynowa i szmaragdowa.
 */
const zastosujKolorMarki = (hex) => {
    const skladowe = kolorMarkiZPanelu(hex) ? naSkladowe(hex) : null;
    const el = document.documentElement;
    // Sprzątanie po poprzednim mechanizmie: kolor marki szedł stylem INLINE na <html>,
    // a inline WYGRYWA z każdą regułą arkusza — więc wbrew zamierzeniu przykrywał także
    // paletę wybraną przez człowieka. Zdejmujemy, gdyby został z poprzedniej wersji.
    el.style.removeProperty("--accent");
    el.style.removeProperty("--accent-rgb");
    el.style.removeProperty("--accent-on");

    let tag = document.getElementById("branding-accent-style");
    if (!skladowe) {
        // Brak koloru albo zapis, którego nie rozumiemy → zdejmujemy nadpisanie
        // i wracamy do wartości domyślnych z arkusza. Cicha zła barwa byłaby gorsza.
        if (tag) tag.remove();
        return;
    }
    const kolor = naHex(naRgb(hex));
    if (!tag) {
        tag = document.createElement("style");
        tag.id = "branding-accent-style";
        document.head.appendChild(tag);
    }
    /* Hierarchia koloru w CSS zamiast w JS: kolor marki stosuje się TYLKO, gdy ani
       człowiek, ani jego spółka nie wskazali palety (data-accent-src ustawia
       PersonalizationContext). Selektor html:not():not() ma specyficzność (0,2,1),
       więc bije bazowe :root, a przy src=user/company w ogóle nie pasuje —
       wtedy rządzi paleta html[data-accent="…"].

       [O1, 2026-09-13] DWIE poprawki czytelności:
       1. Atrament na kolorze marki jest LEPSZYM z dwóch (zmierzony kontrast WCAG), a nie wynikiem
          progu `jasność > 0,5` na składowych bez linearyzacji — ten próg dawał biel 3,68:1 na
          #0891b2 i #3b82f6, gdy ciemny atrament miał tam 4,6:1.
       2. Kolor marki nie miał WERSJI CIEMNEJ: granat albo grafit marki leżał w ciemnym motywie na
          ciemnej karcie z kontrastem ~1,1:1. Druga reguła (specyficzność (0,3,1), bije też ciemny
          blok palety z accentCiemny.css) niesie komplet tokenów z utils/kontrastKolorow.js —
          ten sam odcień dociągnięty do >= 4,5:1 na każdej ciemnej powierzchni, razem z najazdem
          i kafelkiem, żeby nie przeciekały do niego wartości palety domyślnej. */
    const ciemne = tokenyAkcentuCiemnego(kolor);
    tag.textContent =
        `html:not([data-accent-src="user"]):not([data-accent-src="company"]) {` +
        ` --accent: ${kolor}; --accent-rgb: ${skladowe};` +
        ` --accent-on: ${lepszyAtrament(kolor, [ATRAMENT_JASNY, ATRAMENT_CIEMNY_JASNEGO_MOTYWU])}; }\n` +
        `html[data-theme="dark"]:not([data-accent-src="user"]):not([data-accent-src="company"]) {` +
        ` ${jakoDeklaracje(ciemne)} }`;
};

const DEFAULTS = { appName: "mPracownik", logoUrl: "/logo.png" };

export const BrandingProvider = ({ children }) => {
    const [branding, setBranding] = useState(null);
    const [loaded, setLoaded] = useState(false);

    const apply = useCallback((b) => {
        if (!b) return;

        if (b.appName) {
            document.title = b.appName;
            setMeta('meta[property="og:site_name"]', b.appName);
        }
        setMeta('meta[name="description"]', b.metaDescription);
        /* Pasek przeglądarki: prawdziwy kolor marki z panelu wygrywa (znacznik dostaje atrybut, którego motyw nie rusza);
           czerń/biel albo brak = pasek za motywem (jasny/ciemny), tak jak przed wczytaniem brandingu. */
        const kolorPaska = kolorMarkiZPanelu(b.themeColor);
        const metaPaska = document.head.querySelector('meta[name="theme-color"]');
        if (metaPaska && kolorPaska) {
            metaPaska.setAttribute(ATRYBUT_KOLORU_MARKI, "");
            metaPaska.setAttribute("content", kolorPaska);
        } else if (metaPaska) {
            metaPaska.removeAttribute(ATRYBUT_KOLORU_MARKI);
            ustawKolorPaskaPrzegladarki(document.documentElement.getAttribute("data-theme"));
        }

        /* KOLOR MOTYWU STERUJE TAKŻE INTERFEJSEM, nie tylko paskiem przeglądarki.
           BYŁO ŹLE: `themeColor` z panelu Branding trafiał WYŁĄCZNIE do znacznika
           `theme-color`, czyli barwił pasek przeglądarki na telefonie — i nic poza tym.
           SKUTEK DLA UŻYTKOWNIKA: administrator ustawiał kolor firmowy, a przyciski
           nawigacji, akcje główne i widget Teams zostawały w kolorze wpisanym na sztywno
           w kodzie. Wyglądało to tak, jakby ustawienie nie działało.
           Kolejność ważności jest teraz: kolor z brandingu = DOMYŚLNY dla aplikacji,
           a osobisty wybór palety (html[data-accent]) go nadpisuje — bo paleta jest
           deklaracją użytkownika, a branding ustawieniem instytucji. */
        zastosujKolorMarki(b.themeColor);

        if (b.ogTitle) {
            setMeta('meta[property="og:title"]', b.ogTitle);
            setMeta('meta[name="twitter:title"]', b.ogTitle);
        }
        if (b.ogDescription != null) {
            setMeta('meta[property="og:description"]', b.ogDescription);
            setMeta('meta[name="twitter:description"]', b.ogDescription);
        }
        const ogImg = absUrl(b.ogImageUrl);
        if (ogImg) {
            setMeta('meta[property="og:image"]', ogImg);
            setMeta('meta[name="twitter:image"]', ogImg);
        }

        const fav = absUrl(b.faviconUrl);
        if (fav) document.querySelectorAll('link[rel="icon"]').forEach((l) => { l.href = fav; });
        const icon = absUrl(b.iconUrl);
        if (icon) document.querySelectorAll('link[rel="apple-touch-icon"]').forEach((l) => { l.href = icon; });
    }, []);

    const load = useCallback(async () => {
        try {
            const res = await apiService.getBranding();
            setBranding(res.data);
            apply(res.data);
        } catch {
            // brak/niedostępny branding — zostają domyślne z index.html
        } finally {
            setLoaded(true);
        }
    }, [apply]);

    useEffect(() => { load(); }, [load]);

    const value = useMemo(() => ({
        branding,
        loaded,
        appName: branding?.appName || DEFAULTS.appName,
        logoUrl: absUrl(branding?.logoUrl) || DEFAULTS.logoUrl,
        // [BRANDING PER FIRMA] mapa companyId → id palety akcentu; PersonalizationContext
        // stosuje ją jako kolor STARTOWY dla pracownika, który nie wybrał własnego.
        companyAccents: branding?.companyAccents || {},
        // [K 2026-08-31] Odnosniki spolecznosciowe stopki menu - zarzadzane z /admin/branding.
        // Pusta tablica jest STANEM POPRAWNYM: znaczy "nie pokazuj zadnej ikony".
        socialLinks: branding?.socialLinks || [],
        refresh: load,
    }), [branding, loaded, load]);

    return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => useContext(BrandingContext) || {
    branding: null, loaded: false, appName: DEFAULTS.appName, logoUrl: DEFAULTS.logoUrl,
    companyAccents: {}, socialLinks: [], refresh: () => {},
};

export default BrandingContext;
